import { useMemo } from 'react';

/**
 * A deliberately small Markdown renderer for CMS pages and blog articles.
 *
 * It handles exactly what the editor produces — headings, paragraphs, bold,
 * italics, strikethrough, inline code, links, both list kinds, blockquotes,
 * images, code blocks and rules — and nothing else. That is the point.
 *
 * ── WHY NOT A REAL PARSER ───────────────────────────────────────────────────
 *
 * The alternative is shipping a full Markdown parser plus an HTML sanitiser to
 * every buyer on a metered connection in order to render some legal pages and a
 * blog. It is also a far smaller attack surface: this never produces raw HTML
 * from input, so there is no `dangerouslySetInnerHTML` anywhere in the app and
 * no way for markup pasted into the editor to become live markup in a reader's
 * browser.
 *
 * That last property is the reason this file exists rather than a WYSIWYG. A
 * rich-text editor stores HTML, and HTML from a content writer's clipboard is
 * untrusted input that has to be sanitised correctly for ever. Markdown stored
 * as text and rendered into React nodes cannot inject anything, by
 * construction — there is no code path from content to markup.
 *
 * ── TWO TYPOGRAPHIC SCALES ──────────────────────────────────────────────────
 *
 * `variant="page"` is the legal-page scale: compact, dense, meant to be
 * scanned for a clause. `variant="article"` is the reading scale: larger type,
 * more leading, wider spacing between blocks. Same parser, same safety
 * property, different measure — because a returns policy and a thousand-word
 * article are not read the same way.
 */

type Block =
  | { kind: 'heading'; level: 2 | 3 | 4; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'quote'; lines: string[] }
  | { kind: 'code'; text: string }
  | { kind: 'image'; src: string; alt: string }
  | { kind: 'rule' };

type Variant = 'page' | 'article';

/** The two scales, in one place so they can be compared rather than hunted. */
const SCALE: Record<Variant, Record<string, string>> = {
  page: {
    h2: 'mb-2.5 mt-7 font-display text-[19px] font-bold tracking-[-0.015em] first:mt-0',
    h3: 'mb-2 mt-6 font-display text-[15px] font-bold tracking-[-0.01em] first:mt-0',
    h4: 'mb-1.5 mt-5 font-display text-[13.5px] font-bold first:mt-0',
    p: 'my-3 text-[14px] leading-relaxed text-muted',
    list: 'my-3 space-y-1.5 pl-5 text-[14px] leading-relaxed text-muted',
  },
  article: {
    h2: 'mb-3 mt-9 font-display text-[22px] font-bold leading-snug tracking-[-0.02em] text-ink first:mt-0 sm:text-[25px]',
    h3: 'mb-2 mt-7 font-display text-[17px] font-bold leading-snug tracking-[-0.015em] text-ink first:mt-0 sm:text-[19px]',
    h4: 'mb-1.5 mt-6 font-display text-[15px] font-bold text-ink first:mt-0',
    p: 'my-4 text-[15.5px] leading-[1.75] text-ink/80 sm:text-[16.5px]',
    list: 'my-4 space-y-2 pl-6 text-[15.5px] leading-[1.75] text-ink/80 sm:text-[16.5px]',
  },
};

export function Markdown({
  source,
  className,
  variant = 'page',
}: {
  source: string;
  className?: string;
  variant?: Variant;
}) {
  const blocks = useMemo(() => parse(source), [source]);
  const s = SCALE[variant];

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === 'rule') {
          return <hr key={index} className="my-8 border-0 border-t border-line" />;
        }

        if (block.kind === 'image') {
          return (
            <figure key={index} className="my-6">
              {/* `loading="lazy"` because an article can carry several and the
                  ones below the fold are not worth a reader's data until they
                  scroll to them. */}
              <img
                src={block.src}
                alt={block.alt}
                loading="lazy"
                className="w-full rounded-card border border-line"
              />
              {block.alt ? (
                <figcaption className="mt-2 text-center text-[12px] text-faint">
                  {block.alt}
                </figcaption>
              ) : null}
            </figure>
          );
        }

        if (block.kind === 'code') {
          return (
            <pre
              key={index}
              className="my-5 overflow-x-auto rounded-card border border-line bg-shell p-3.5 font-mono text-[12.5px] leading-relaxed text-ink"
            >
              <code>{block.text}</code>
            </pre>
          );
        }

        if (block.kind === 'quote') {
          return (
            <blockquote
              key={index}
              className="my-5 border-0 border-l-[3px] border-blue pl-4 text-[15.5px] font-medium italic leading-relaxed text-ink/75"
            >
              {block.lines.map((line, lineIndex) => (
                <p key={lineIndex} className="m-0 mt-2 first:mt-0">
                  {inline(line)}
                </p>
              ))}
            </blockquote>
          );
        }

        if (block.kind === 'heading') {
          const text = inline(block.text);

          if (block.level === 2) {
            return (
              <h2 key={index} className={s.h2}>
                {text}
              </h2>
            );
          }

          return block.level === 3 ? (
            <h3 key={index} className={s.h3}>
              {text}
            </h3>
          ) : (
            <h4 key={index} className={s.h4}>
              {text}
            </h4>
          );
        }

        if (block.kind === 'list') {
          const items = block.items.map((item, itemIndex) => (
            <li key={itemIndex}>{inline(item)}</li>
          ));

          return block.ordered ? (
            <ol key={index} className={`list-decimal ${s.list}`}>
              {items}
            </ol>
          ) : (
            <ul key={index} className={`list-disc ${s.list}`}>
              {items}
            </ul>
          );
        }

        return (
          <p key={index} className={s.p}>
            {inline(block.text)}
          </p>
        );
      })}
    </div>
  );
}

function parse(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n/g, '\n').split('\n');

  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let quote: string[] | null = null;
  let fence: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };

  const flushList = () => {
    if (list) {
      blocks.push({ kind: 'list', ordered: list.ordered, items: list.items });
      list = null;
    }
  };

  const flushQuote = () => {
    if (quote) {
      blocks.push({ kind: 'quote', lines: quote });
      quote = null;
    }
  };

  const flushAll = () => {
    flushParagraph();
    flushList();
    flushQuote();
  };

  for (const raw of lines) {
    const line = raw.trimEnd();

    // A fence swallows everything until it closes, including what would
    // otherwise be headings and list markers. That is the whole point of one.
    if (/^\s*```/.test(line)) {
      if (fence) {
        blocks.push({ kind: 'code', text: fence.join('\n') });
        fence = null;
      } else {
        flushAll();
        fence = [];
      }
      continue;
    }

    if (fence) {
      fence.push(raw);
      continue;
    }

    if (line.trim() === '') {
      flushAll();
      continue;
    }

    if (/^\s*([-*_])\1{2,}\s*$/.test(line)) {
      flushAll();
      blocks.push({ kind: 'rule' });
      continue;
    }

    // An image on its own line is a figure. One inside a sentence stays inline
    // text, because a 600px picture mid-paragraph is not what anybody meant.
    const image = /^\s*!\[([^\]]*)\]\(([^)\s]+)\)\s*$/.exec(line);

    if (image && safeSrc(image[2]!)) {
      flushAll();
      blocks.push({ kind: 'image', src: image[2]!, alt: image[1] ?? '' });
      continue;
    }

    const quoted = /^\s*>\s?(.*)$/.exec(line);

    if (quoted) {
      flushParagraph();
      flushList();
      quote ??= [];
      quote.push(quoted[1] ?? '');
      continue;
    }

    const heading = /^(#{2,4})\s+(.*)$/.exec(line);

    if (heading) {
      flushAll();
      blocks.push({
        kind: 'heading',
        level: heading[1]!.length as 2 | 3 | 4,
        text: heading[2]!,
      });
      continue;
    }

    const ordered = /^\s*\d+\.\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);

    if (ordered || bullet) {
      flushParagraph();
      flushQuote();

      const isOrdered = Boolean(ordered);
      const text = (ordered?.[1] ?? bullet?.[1])!;

      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = { ordered: isOrdered, items: [] };
      }

      list.items.push(text);
      continue;
    }

    flushList();
    flushQuote();
    paragraph.push(line.trim());
  }

  if (fence) blocks.push({ kind: 'code', text: fence.join('\n') });
  flushAll();

  return blocks;
}

/** http(s) and in-app paths only. A `javascript:` or `data:` URL is not a picture. */
function safeSrc(src: string): boolean {
  return /^(https?:\/\/|\/)/i.test(src);
}

/**
 * Inline formatting, returned as React nodes rather than an HTML string. There
 * is no path from content to raw markup, by construction.
 */
function inline(text: string): React.ReactNode[] {
  const pattern = /(\*\*[^*]+\*\*|~~[^~]+~~|\*[^*]+\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;

  return text
    .split(pattern)
    .filter(Boolean)
    .map((token, index) => {
      if (token.startsWith('**') && token.endsWith('**')) {
        return (
          <strong key={index} className="font-bold text-ink">
            {token.slice(2, -2)}
          </strong>
        );
      }

      if (token.startsWith('~~') && token.endsWith('~~')) {
        return (
          <s key={index} className="text-faint">
            {token.slice(2, -2)}
          </s>
        );
      }

      if (token.startsWith('`') && token.endsWith('`') && token.length > 2) {
        return (
          <code
            key={index}
            className="rounded-[4px] bg-shell px-1 py-0.5 font-mono text-[0.9em] text-ink"
          >
            {token.slice(1, -1)}
          </code>
        );
      }

      if (token.startsWith('*') && token.endsWith('*')) {
        return <em key={index}>{token.slice(1, -1)}</em>;
      }

      const link = /^\[([^\]]+)\]\(([^)]+)\)$/.exec(token);

      if (link) {
        const href = link[2]!;
        // Only http(s) and in-app links render as links. `javascript:` never does.
        const external = /^https?:\/\//i.test(href);

        return safeSrc(href) ? (
          <a
            key={index}
            href={href}
            {...(external ? { rel: 'noreferrer noopener', target: '_blank' } : {})}
            className="font-medium text-blue underline"
          >
            {link[1]}
          </a>
        ) : (
          <span key={index}>{link[1]}</span>
        );
      }

      return <span key={index}>{token}</span>;
    });
}
